import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, type NavigateOptions, type To } from "react-router-dom";
import {
  createForm,
  deleteForm,
  fetchFormForEdit,
  replaceForm,
  updateForm,
} from "@/api/forms";
import { generateFormFields } from "@/api/generateFields";
import { ApiError } from "@/api/client";
import {
  copyDraftForNewForm,
  createEmptyDraft,
  createEmptyField,
  createEmptySection,
  draftFromForm,
  draftToSavePayload,
  draftToYandexSavePayload,
  mergeSubmitHooksTextAfterSave,
  submitHooksTextFromForm,
  validateDraft,
  validateYandexDraft,
  verifyDraftFieldsPreserved,
} from "@/lib/formBuilder/draft";
import { applyGeneratedFieldsToDraft } from "@/lib/formBuilder/generateFields";
import {
  addPrivacyFieldToDraft,
  collectPersonalDataTypes,
  shouldSuggestPrivacyField,
} from "@/lib/formBuilder/privacySuggestion";
import {
  isGlobalManager,
  isYandexForm,
  type FormSource,
} from "@/lib/forms/formSource";
import { formatSubmitError } from "@/lib/http/formatSubmitError";
import type {
  FormBuilderDraft,
  FormBuilderField,
  FormBuilderSection,
} from "@/types/formBuilder";
import { useAuth } from "@/contexts/AuthProvider";
import { useFormFieldTypes } from "@/hooks/useFormFieldTypes";
import {
  countSubmitHooks,
  duplicateFieldInDraft,
  moveFieldInDraft,
  moveSectionInDraft,
  reorder,
  withFieldOrder,
  withSectionOrder,
} from "./builderUtils";
import type { BuilderTab, FormBuilderMeta } from "./types";
import { formMetaFromForm } from "./formBuilderMeta";

interface UseFormBuilderOptions {
  formId: string | undefined;
}

interface SaveFormOptions {
  navigateAfterCreate?: boolean;
}

export function useFormBuilder({ formId }: UseFormBuilderOptions) {
  const navigate = useNavigate();
  const isNew = !formId;
  const numericId = formId ? Number(formId) : null;

  const [draft, setDraft] = useState<FormBuilderDraft>(createEmptyDraft);
  const [isLoading, setLoading] = useState(!isNew);
  const [isSaving, setSaving] = useState(false);
  const [isDuplicating, setDuplicating] = useState(false);
  const [isDeleting, setDeleting] = useState(false);
  const [isDirty, setDirty] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveFailed, setSaveFailed] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [activeTab, setActiveTab] = useState<BuilderTab>("form");
  const [privacySuggestionDismissed, setPrivacySuggestionDismissed] =
    useState(false);
  const [isGenerating, setGenerating] = useState(false);
  const [generateError, setGenerateError] = useState<string | null>(null);
  const [createSource, setCreateSource] = useState<FormSource>("native");
  const [formMeta, setFormMeta] = useState<FormBuilderMeta>({
    source: "native",
    publicId: null,
    editorUrl: null,
    publicUrl: null,
    organisationId: null,
    externalServiceId: null,
    externalServiceName: null,
    yandexSurveyId: null,
    createdAt: null,
    updatedAt: null,
  });
  const skipNavigationGuardRef = useRef(false);

  const navigateWithoutGuard = useCallback(
    (to: To, options?: NavigateOptions) => {
      skipNavigationGuardRef.current = true;
      navigate(to, options);
      window.setTimeout(() => {
        skipNavigationGuardRef.current = false;
      }, 0);
    },
    [navigate],
  );

  const { fieldTypes } = useFormFieldTypes();
  const {
    roles,
    canCreateForms,
    canManageForm: authCanManageForm,
    canViewForm: authCanViewForm,
  } = useAuth();

  const isYandex = isNew ? createSource === "yandex" : isYandexForm(formMeta);
  const canManage = isNew
    ? isYandex
      ? isGlobalManager(roles) ||
        (formMeta.organisationId != null &&
          authCanManageForm(formMeta.organisationId))
      : canCreateForms
    : authCanManageForm(formMeta.organisationId) ||
      (isYandex && formMeta.organisationId == null && isGlobalManager(roles));

  const hooksCount = countSubmitHooks(draft.submitHooksText);
  const showPrivacySuggestion =
    !isYandex &&
    activeTab === "form" &&
    !privacySuggestionDismissed &&
    shouldSuggestPrivacyField(draft);
  const detectedPersonalDataTypes = collectPersonalDataTypes(draft);

  useEffect(() => {
    document.title = isNew ? "Новая форма" : "Редактирование формы";
  }, [isNew]);

  useEffect(() => {
    if (isNew || !numericId || Number.isNaN(numericId)) {
      if (!isNew) setLoadError("Некорректный идентификатор формы");
      setLoading(false);
      return;
    }

    let cancelled = false;

    (async () => {
      setLoading(true);
      setLoadError(null);

      try {
        const form = await fetchFormForEdit(numericId);
        const orgId = form.organisationId ?? null;
        if (isYandexForm(form) && orgId == null && !isGlobalManager(roles)) {
          if (!cancelled) {
            setLoadError("Нет доступа: форма не привязана к организации");
          }
          return;
        }
        if (!authCanViewForm(orgId)) {
          if (!cancelled) {
            setLoadError("Нет доступа к форме");
          }
          return;
        }
        if (!cancelled) {
          setDraft(draftFromForm(form));
          setFormMeta(formMetaFromForm(form));
          setDirty(false);
        }
      } catch (err) {
        if (!cancelled) {
          setLoadError(
            err instanceof ApiError
              ? err.message
              : "Не удалось загрузить форму",
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isNew, numericId, authCanViewForm, roles]);

  const updateDraft = (patch: Partial<FormBuilderDraft>) => {
    setDirty(true);
    setDraft((prev) => ({ ...prev, ...patch }));
  };

  const updateSection = (
    sectionClientId: string,
    patch: Partial<FormBuilderSection>,
  ) => {
    setDirty(true);
    setDraft((prev) => ({
      ...prev,
      sections: prev.sections.map((section) =>
        section.clientId === sectionClientId
          ? { ...section, ...patch }
          : section,
      ),
    }));
  };

  const updateField = (
    sectionClientId: string,
    fieldClientId: string,
    patch: Partial<FormBuilderField>,
  ) => {
    setDirty(true);
    setDraft((prev) => ({
      ...prev,
      sections: prev.sections.map((section) => {
        if (section.clientId !== sectionClientId) return section;

        return {
          ...section,
          fields: section.fields.map((field) => {
            if (field.clientId !== fieldClientId) return field;
            return { ...field, ...patch };
          }),
        };
      }),
    }));
  };

  const addField = (sectionClientId: string) => {
    setDirty(true);
    setDraft((prev) => {
      const existingNames = prev.sections.flatMap((section) =>
        section.fields.map((field) => field.name).filter(Boolean),
      );

      return {
        ...prev,
        sections: prev.sections.map((section) => {
          if (section.clientId !== sectionClientId) return section;
          return {
            ...section,
            fields: withFieldOrder([
              ...section.fields,
              createEmptyField(section.fields.length, existingNames),
            ]),
          };
        }),
      };
    });
  };

  const removeField = (sectionClientId: string, fieldClientId: string) => {
    setDirty(true);
    setDraft((prev) => ({
      ...prev,
      sections: prev.sections.map((section) => {
        if (section.clientId !== sectionClientId) return section;
        return {
          ...section,
          fields: withFieldOrder(
            section.fields.filter((field) => field.clientId !== fieldClientId),
          ),
        };
      }),
    }));
  };

  const moveField = (
    sectionClientId: string,
    fieldClientId: string,
    direction: -1 | 1,
  ) => {
    setDirty(true);
    setDraft((prev) => ({
      ...prev,
      sections: prev.sections.map((section) => {
        if (section.clientId !== sectionClientId) return section;
        const index = section.fields.findIndex(
          (field) => field.clientId === fieldClientId,
        );
        if (index < 0) return section;
        return {
          ...section,
          fields: withFieldOrder(
            reorder(section.fields, index, index + direction),
          ),
        };
      }),
    }));
  };

  const moveFieldToSection = (
    fieldClientId: string,
    sectionClientId: string,
    targetIndex: number,
  ) => {
    setDirty(true);
    setDraft((prev) =>
      moveFieldInDraft(prev, fieldClientId, sectionClientId, targetIndex),
    );
  };

  const duplicateField = (sectionClientId: string, fieldClientId: string) => {
    setDirty(true);
    setDraft((prev) =>
      duplicateFieldInDraft(prev, sectionClientId, fieldClientId),
    );
  };

  const moveSection = (sectionClientId: string, direction: -1 | 1) => {
    setDirty(true);
    setDraft((prev) => moveSectionInDraft(prev, sectionClientId, direction));
  };

  const addSection = () => {
    setDirty(true);
    setDraft((prev) => ({
      ...prev,
      sections: withSectionOrder([
        ...prev.sections,
        createEmptySection(prev.sections.length),
      ]),
    }));
  };

  const removeSection = (sectionClientId: string) => {
    setDirty(true);
    setDraft((prev) => {
      if (prev.sections.length <= 1) return prev;
      return {
        ...prev,
        sections: withSectionOrder(
          prev.sections.filter(
            (section) => section.clientId !== sectionClientId,
          ),
        ),
      };
    });
  };

  const handleSave = async (
    options: SaveFormOptions = {},
  ): Promise<boolean> => {
    if (!canManage) return false;

    setSaveFailed(false);
    const errors = isYandex
      ? validateYandexDraft(draft)
      : validateDraft(draft, { fieldTypes });
    setValidationErrors(errors);
    setSaveError(null);

    if (errors.length > 0) {
      setSaveFailed(true);
      if (
        !isYandex &&
        errors.some((message) => message.toLowerCase().includes("submit hooks"))
      ) {
        setActiveTab("hooks");
      }
      return false;
    }

    if (
      !isYandex &&
      formMeta.organisationId == null &&
      !isGlobalManager(roles)
    ) {
      setValidationErrors(["Выберите организацию"]);
      setSaveFailed(true);
      return false;
    }

    const beforeSave = draft;
    setSaving(true);

    try {
      if (isNew) {
        if (isYandex) {
          const created = await createForm({
            source: "yandex",
            name: draft.name.trim(),
            description: draft.description.trim(),
            isClosed: draft.isClosed,
            closedMessage: draft.isClosed
              ? draft.closedMessage.trim() || null
              : null,
            organisationId: formMeta.organisationId,
            fields: [],
          });
          setDirty(false);
          if (options.navigateAfterCreate !== false) {
            navigateWithoutGuard(`/admin/forms/${created.id}/edit`, {
              replace: true,
            });
          }
          return true;
        }

        const payload = draftToSavePayload(draft, {
          fieldTypes,
          organisationId: formMeta.organisationId,
          externalServiceId: formMeta.externalServiceId,
        });
        const created = await createForm(payload);
        setDirty(false);
        if (options.navigateAfterCreate !== false) {
          navigateWithoutGuard(`/admin/forms/${created.id}/edit`, {
            replace: true,
          });
        }
        return true;
      } else if (numericId) {
        if (isYandex) {
          const payload = draftToYandexSavePayload(
            draft,
            formMeta.organisationId,
          );
          await updateForm(numericId, payload);
          const reloaded = await fetchFormForEdit(numericId);
          setDraft(draftFromForm(reloaded));
          setFormMeta(formMetaFromForm(reloaded));
          setDirty(false);
          return true;
        }

        const payload = draftToSavePayload(draft, {
          fieldTypes,
          organisationId: formMeta.organisationId,
          externalServiceId: formMeta.externalServiceId,
        });
        await replaceForm(numericId, payload);
        const reloaded = await fetchFormForEdit(numericId);
        const reloadedDraft = draftFromForm(reloaded);
        if (payload.submitHooks) {
          reloadedDraft.submitHooksText = submitHooksTextFromForm(
            payload.submitHooks,
          );
        } else {
          reloadedDraft.submitHooksText = mergeSubmitHooksTextAfterSave(
            beforeSave.submitHooksText,
            reloadedDraft.submitHooksText,
          );
        }
        const preservationError = verifyDraftFieldsPreserved(
          beforeSave,
          reloadedDraft,
        );
        if (preservationError) {
          setSaveError(preservationError);
          setSaveFailed(true);
          return false;
        }
        setDraft(reloadedDraft);
        setFormMeta(formMetaFromForm(reloaded));
        setDirty(false);
        return true;
      }
    } catch (err) {
      setSaveError(formatSubmitError(err));
      setSaveFailed(true);
      return false;
    } finally {
      setSaving(false);
    }

    return false;
  };

  const allFieldNames = draft.sections.flatMap((section) =>
    section.fields.map((field) => field.name).filter(Boolean),
  );

  const allBuilderFields = draft.sections.flatMap((section) => section.fields);

  const formFields = draft.sections.flatMap((section) =>
    section.fields
      .filter((field) => field.name.trim())
      .map((field) => ({
        name: field.name,
        label: field.label,
        type: field.type,
      })),
  );

  const fioFieldNames = draft.sections.flatMap((section) =>
    section.fields
      .filter((field) => field.type === "fio")
      .map((field) => field.name)
      .filter(Boolean),
  );

  const handleDuplicateForm = async () => {
    if (isNew || isDuplicating || isSaving || isLoading) return;

    const copyDraft = copyDraftForNewForm(draft);
    const errors = validateDraft(copyDraft, { fieldTypes });
    if (errors.length > 0) {
      setSaveFailed(false);
      setSaveError(`Не удалось скопировать форму: ${errors[0]}`);
      return;
    }

    setDuplicating(true);
    setSaveFailed(false);
    setSaveError(null);

    try {
      const created = await createForm(
        draftToSavePayload(copyDraft, { fieldTypes }),
      );
      navigateWithoutGuard(`/admin/forms/${created.id}/edit`);
    } catch (err) {
      setSaveError(formatSubmitError(err));
    } finally {
      setDuplicating(false);
    }
  };

  const handleDeleteForm = async () => {
    if (isNew || !numericId || isDeleting || isSaving) return;

    const formName = draft.name.trim() || "Форма";
    if (
      !window.confirm(
        `Удалить форму «${formName}»? Ответы и поля будут удалены безвозвратно.`,
      )
    ) {
      return;
    }

    setDeleting(true);
    setSaveFailed(false);
    setSaveError(null);

    try {
      await deleteForm(numericId);
      setDirty(false);
      navigateWithoutGuard("/");
    } catch (err) {
      setSaveError(formatSubmitError(err));
    } finally {
      setDeleting(false);
    }
  };

  const handleAddPrivacyField = () => {
    setDirty(true);
    setDraft((prev) => addPrivacyFieldToDraft(prev));
    setPrivacySuggestionDismissed(true);
  };

  const handleGenerateFields = async (
    prompt: string,
    mode: "replace" | "append",
  ) => {
    setGenerateError(null);
    setGenerating(true);

    try {
      const generated = await generateFormFields({
        prompt,
        name: draft.name.trim() || undefined,
      });

      setDirty(true);
      setDraft((prev) => applyGeneratedFieldsToDraft(prev, generated, mode));
      setPrivacySuggestionDismissed(false);
      setValidationErrors([]);
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 503) {
          setGenerateError(
            "AI-генерация недоступна: на сервере не настроен DeepSeek.",
          );
        } else if (err.status === 502) {
          setGenerateError(
            "Не удалось получить ответ от AI. Попробуйте ещё раз.",
          );
        } else {
          setGenerateError(formatSubmitError(err));
        }
      } else {
        setGenerateError(
          err instanceof Error ? err.message : "Не удалось сгенерировать поля",
        );
      }
    } finally {
      setGenerating(false);
    }
  };

  const setOrganisationId = useCallback(
    (organisationId: number | null, options: { markDirty?: boolean } = {}) => {
      if (options.markDirty !== false) setDirty(true);
      setFormMeta((prev) => ({ ...prev, organisationId }));
    },
    [],
  );

  const setExternalServiceId = useCallback(
    (
      externalServiceId: number | null,
      options: { markDirty?: boolean } = {},
    ) => {
      if (options.markDirty !== false) setDirty(true);
      setFormMeta((prev) => ({
        ...prev,
        externalServiceId,
        externalServiceName:
          externalServiceId === prev.externalServiceId
            ? prev.externalServiceName
            : null,
      }));
    },
    [],
  );

  const updateCreateSource = useCallback((source: FormSource) => {
    setCreateSource(source);
    setDirty(true);
  }, []);

  const reportActionError = useCallback((message: string | null) => {
    setSaveFailed(false);
    setSaveError(message);
  }, []);

  return {
    isNew,
    numericId,
    draft,
    isLoading,
    isSaving,
    isDuplicating,
    isDeleting,
    isDirty,
    skipNavigationGuardRef,
    loadError,
    saveError,
    saveFailed,
    setSaveError: reportActionError,
    validationErrors,
    activeTab,
    setActiveTab,
    isYandex,
    canManage,
    hooksCount,
    showPrivacySuggestion,
    detectedPersonalDataTypes,
    isGenerating,
    generateError,
    createSource,
    setCreateSource: updateCreateSource,
    formMeta,
    setOrganisationId,
    setExternalServiceId,
    fieldTypes,
    roles,
    updateDraft,
    updateSection,
    updateField,
    addField,
    removeField,
    moveField,
    moveFieldToSection,
    duplicateField,
    moveSection,
    addSection,
    removeSection,
    handleSave,
    allFieldNames,
    allBuilderFields,
    formFields,
    fioFieldNames,
    handleDuplicateForm,
    handleDeleteForm,
    handleAddPrivacyField,
    handleGenerateFields,
    dismissPrivacySuggestion: () => setPrivacySuggestionDismissed(true),
  };
}
